import * as React from 'react';
import { IgcBannerComponent as Component } from 'igniteui-webcomponents';
import type { IgcBannerComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * A non-modal notification banner that displays important, concise messages
 * requiring user acknowledgement.
 *
 * The banner slides into view with an animated grow transition and renders
 * inline, pushing the surrounding page content rather than overlaying it.
 *
 * The component integrates with the
 * [Invoker Commands API](https://developer.mozilla.org/en-US/docs/Web/API/Invoker_Commands_API):
 * an Ignite button or a native `<button>` with `command="--show"` / `"--hide"` /
 * `"--toggle"` and `commandfor` pointing to this element will call the
 * corresponding method declaratively without any JavaScript.
 * @slot  - The banner message text content.
 * @slot prefix - An icon or illustration rendered to the left of the message. Useful for reinforcing the message type (info, warning, success, etc.).
 * @slot actions - Custom action elements rendered in the banner's action area. When provided, replaces the default "OK" dismiss button.
 * @fires igcClosing - Emitted just before the banner closes in response to the default action button being clicked. Cancelable — call `event.preventDefault()` to abort the closing sequence.
 * @fires igcClosed - Emitted after the banner has fully closed and its exit animation has completed.
 * @csspart base - The root wrapper element of the banner.
 * @csspart spacer - The inner wrapper that controls the spacing around the banner content.
 * @csspart message - The container that holds the illustration and text content.
 * @csspart illustration - The container for the prefix slot (icon/illustration).
 * @csspart content - The container for the default message slot.
 * @csspart actions - The container for the action buttons slot.
 * @class
 */
export const IgrBanner = /* @__PURE__ */ createComponent({
  tagName: 'igc-banner',
  displayName: 'IgrBanner',
  elementClass: Component,
  react: React,
  events: {
    onClosing: 'igcClosing' as EventName<
      IgcBannerComponentEventMap['igcClosing']
    >,
    onClosed: 'igcClosed' as EventName<IgcBannerComponentEventMap['igcClosed']>,
  },
});

export type IgrBanner = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrBannerModule = Component;
